import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createProjectTemplate,
  getProjectTemplate,
  getProjectTemplates,
  softDeleteProjectTemplate,
  updateProjectTemplate,
  type CreateProjectTemplateInput,
  type ProjectTemplate,
  type ProjectTemplateDetail,
  type ProjectTemplateState,
  type UpdateProjectTemplateInput,
} from '../services/api/projectTemplates';

/**
 * Fetch active (non-legacy, non-deleted) project templates.
 *
 * Used by `ProjectFormPage.tsx` (plan 19-06) to populate the Template
 * dropdown on the create path. The default selection is the Minimal
 * built-in template — the page derives that from this list, NOT from a
 * separate hook (per 19-CONTEXT.md D-06).
 *
 * `enabled` defaults to true (admin-page callers); pass `false` to
 * suppress the request when the current user is not an admin (Phase 20
 * BL-03 — endpoint is gated by `requireAdmin`, so non-admin PMs would
 * otherwise emit 403s on every page load).
 */
export function useProjectTemplates(enabled: boolean = true) {
  return useQuery({
    queryKey: ['projectTemplates'],
    queryFn: () => getProjectTemplates(),
    enabled,
  });
}

// Re-export types for downstream consumers (plan 19-06 imports `ProjectTemplate`
// here rather than reaching into the api module — matches the existing
// re-export pattern in useProjects.ts).
export type { ProjectTemplate, ProjectTemplateDetail, ProjectTemplateState };

/**
 * Fetch a single template by id. Backend response is the canonical
 * ProjectTemplate shape (no inUseCount on the wire — see plan 20-02
 * Task 1 notes). The TPL-06 disabled-soft-delete tooltip (D-05) is
 * computed client-side in ProjectTemplateEditPage from useProjects().
 */
export function useProjectTemplate(id: number | undefined) {
  return useQuery({
    queryKey: ['projectTemplate', id],
    queryFn: () => getProjectTemplate(id!),
    enabled: !!id,
  });
}

/**
 * Admin: create a new template (TPL-04, D-03 — full-page form Save).
 * Uses simple invalidate-only per D-24 template branch.
 */
export function useCreateProjectTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectTemplateInput) => createProjectTemplate(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectTemplates'] });
    },
  });
}

/**
 * Admin: edit an existing template (TPL-05, D-03). Forked-snapshot —
 * edits do NOT propagate to projects already created from this template.
 */
export function useUpdateProjectTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UpdateProjectTemplateInput }) =>
      updateProjectTemplate(id, patch),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['projectTemplate', variables.id] });
    },
  });
}

/**
 * Admin: soft-delete a non-built-in template (TPL-06, D-05).
 * Server returns 409 if the template is the most-recent-applied of any
 * project; ProjectTemplateEditPage surfaces 409 via AppToast.
 */
export function useSoftDeleteProjectTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => softDeleteProjectTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectTemplates'] });
    },
  });
}
